import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { ApiError } from '../../api/client'
import {
  addRustdeskAccount,
  removeRustdeskAccount,
  rustdeskShellQuery,
  saveRustdesk,
  switchRustdeskTool,
  type RustdeskConfig,
  type RustdeskDevice,
  type RustdeskShell,
  type RustdeskTool,
} from '../../api/settingsRustdesk'
import { DataTable, useTableState, type TableColumn, type TableState } from '../../ui/DataTable'
import { Forbidden } from '../../ui/Forbidden'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'
import '../../ui/SettingsParts.css'
import '../remote_connect/RustdeskOrgPanels.css'
import './RustdeskSettingsPage.css'

/**
 * Settings -> General -> RustDesk servers: setarile RustDesk ale FURNIZORULUI,
 * element cu element fata de `views/version-3/settings/rustdesk/index.php`.
 *
 * PANA PE 6 OCTOMBRIE 2026 ERA ECRANUL `/settings/rustdesk`, cu bara lui
 * ("AGENT SETTINGS"). Din 7 octombrie e intrarea "RustDesk servers" din meniul
 * din stanga al setarilor, la General, sub AI Agent si sub "RustDesk"
 * (setarile organizatiei, care au luat acum adresa `/settings/rustdesk`);
 * pagina e `RustdeskServersSettingsPage`, la `/settings/rustdesk_servers`.
 * Api-ul, dreptul (`settings.my_company.read`, scrierea `.edit`) si ce se
 * salveaza au ramas aceleasi.
 *
 * Lista serverelor furnizorului (`RustdeskProviderServers`) intra prin
 * `servers`, imediat sub randul cu unealta in uz.
 *
 * IN PORTAL E O FILA, AICI E UN ECRAN. Acolo, bucata se aducea prin ajax in
 * coaja setarilor de firma si dupa fiecare salvare se cerea din nou, fiindca
 * butoanele de comutare depind de ce s-a salvat. Aici fiecare scriere raspunde
 * cu ecranul intreg, deci nu mai e a doua cerere - dar se vede acelasi lucru:
 * butonul de RustDesk se deblocheaza in clipa in care configurarea tine.
 *
 * CE E ACTIV ACUM STA SUS DE TOT. Cine intra aici a venit cu o intrebare -
 * "de ce apare butonul vechi?", "am trecut pe RustDesk?" - iar raspunsul
 * trebuie sa fie primul lucru pe care il vede, nu ceva de dedus din bifele de
 * mai jos.
 *
 * NIMIC DIN LISTA DE STATII NU SE SCRIE DE MANA, dinadins: un ID introdus
 * gresit inseamna un buton care se apasa, nu deschide nimic, si nu spune de ce.
 * Lista e o oglinda a ce a trimis agentul.
 */

function failed(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'Something went wrong.'
}

/** `substr(..., 0, 16)` din macheta: "2026-09-08 09:30". */
export function shortStamp(value: string): string {
  return String(value ?? '').substring(0, 16)
}

function ServerCard({ config, onChange }: { config: RustdeskConfig; onChange(next: RustdeskConfig): void }) {
  return (
    <div className="rd-card">
      <h4>Default server</h4>
      <p className="rd-note" style={{ marginBottom: 14 }}>
        Your own RustDesk server, the first one in the list above. <strong>hbbs</strong> keeps the register of stations; <strong>hbbr</strong> relays the
        picture when the two ends cannot reach each other directly.
      </p>
      <div className="row">
        <div className="col-md-5">
          <div className="form-group">
            <label className="control-label" htmlFor="rdHbbs">
              hbbs address
            </label>
            <input
              type="text"
              className="form-control input-sm"
              id="rdHbbs"
              name="hbbs_host"
              placeholder="rustdesk.example.local"
              value={config.hbbs_host}
              onChange={(e) => onChange({ ...config, hbbs_host: e.target.value })}
            />
          </div>
        </div>
        <div className="col-md-2">
          <div className="form-group">
            <label className="control-label" htmlFor="rdHbbsPort">
              port
            </label>
            <input
              type="number"
              className="form-control input-sm"
              id="rdHbbsPort"
              name="hbbs_port"
              value={config.hbbs_port}
              onChange={(e) => onChange({ ...config, hbbs_port: Number(e.target.value) })}
            />
          </div>
        </div>
        <div className="col-md-3">
          <div className="form-group">
            <label className="control-label" htmlFor="rdHbbr">
              hbbr address
            </label>
            <input
              type="text"
              className="form-control input-sm"
              id="rdHbbr"
              name="hbbr_host"
              value={config.hbbr_host}
              onChange={(e) => onChange({ ...config, hbbr_host: e.target.value })}
            />
          </div>
        </div>
        <div className="col-md-2">
          <div className="form-group">
            <label className="control-label" htmlFor="rdHbbrPort">
              port
            </label>
            <input
              type="number"
              className="form-control input-sm"
              id="rdHbbrPort"
              name="hbbr_port"
              value={config.hbbr_port}
              onChange={(e) => onChange({ ...config, hbbr_port: Number(e.target.value) })}
            />
          </div>
        </div>
      </div>
      <div className="form-group">
        <label className="control-label" htmlFor="rdKey">
          Server key
        </label>
        <input
          type="text"
          className="form-control input-sm rd-mono"
          id="rdKey"
          name="server_key"
          placeholder="the public key printed by the server on first start"
          value={config.server_key}
          onChange={(e) => onChange({ ...config, server_key: e.target.value })}
        />
        <span className="rd-note">
          {/*
            Spus pe fata, fiindca e singura setare a carei lipsa nu se vede ca
            o defectiune: totul pare ca merge, doar ca statia se poate lega la
            altcineva.
          */}
          <strong>Required.</strong> Without it a station connects to whichever server answers first, which is the one
          thing this key is there to prevent.
        </span>
      </div>
    </div>
  )
}

/** Cele patru bife, in ordinea din macheta. */
type RustdeskFlag = 'allow_file_transfer' | 'require_recording' | 'allow_stealth' | 'require_consent'

const FLAGS: { name: RustdeskFlag; label: string; note: string }[] = [
  { name: 'allow_file_transfer', label: 'Allow file transfer', note: 'every transfer is written to the record' },
  { name: 'require_recording', label: 'Record every session', note: 'needs the agent; see the notes below' },
  { name: 'allow_stealth', label: 'Allow watching without the person knowing', note: 'each such session is marked in the record' },
  {
    name: 'require_consent',
    label: 'Ask the person before connecting',
    note: 'off by default here: the staff, the machines and the technicians all belong to the same organisation',
  },
]

function SessionCard({ config, onChange }: { config: RustdeskConfig; onChange(next: RustdeskConfig): void }) {
  return (
    <div className="rd-card">
      <h4>What a session is allowed to do</h4>
      <p className="rd-note" style={{ marginBottom: 12 }}>
        Each of these is a separate requirement in the tender, so each can be turned off on its own.
      </p>
      <div className="form-group">
        {FLAGS.map((flag) => (
          <div className="rd-choice" key={String(flag.name)}>
            <label className="od-inv-check">
              <input
                type="checkbox"
                name={String(flag.name)}
                value="1"
                checked={config[flag.name]}
                onChange={(e) => onChange({ ...config, [flag.name]: e.target.checked })}
              />
              <span>
                {flag.label} <span className="rd-note">&mdash; {flag.note}</span>
              </span>
            </label>
          </div>
        ))}
        {config.permanent_password !== undefined && (
          /* CEC 64: v4 intra numai cu parola de unica folosinta. Bifa spune daca
             statia mai tine si o parola permanenta pentru portalul vechi. */
          <div className="rd-choice">
            <label className="od-inv-check">
              <input
                type="checkbox"
                name="permanent_password"
                value="1"
                checked={config.permanent_password}
                onChange={(e) => onChange({ ...config, permanent_password: e.target.checked })}
              />
              <span>
                Keep a permanent station password{' '}
                <span className="rd-note">
                  &mdash; only the old portal uses it; turned off, stations accept one-time passwords only and no
                  password is stored on the server
                </span>
              </span>
            </label>
          </div>
        )}
      </div>
      <div className="row">
        <div className="col-md-3">
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label className="control-label" htmlFor="rdDays">
              Keep recordings for
            </label>
            <div className="input-group input-group-sm">
              <input
                type="number"
                className="form-control"
                id="rdDays"
                name="recording_days"
                min={1}
                max={3650}
                value={config.recording_days}
                onChange={(e) => onChange({ ...config, recording_days: Number(e.target.value) })}
              />
              <span className="input-group-addon">days</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

function AccountsCard({ page, onChanged }: { page: RustdeskShell; onChanged(next: RustdeskShell): void }) {
  const [label, setLabel] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [vault, setVault] = useState('local')
  const [vaultPath, setVaultPath] = useState('')
  const [busy, setBusy] = useState(false)
  const hasHashicorp = (page.vaults ?? []).includes('hashicorp')
  const canAdd = page.canStorePasswords || hasHashicorp

  const add = () => {
    setBusy(true)
    const fields: Record<string, string> = { label, username, password }
    if (vault === 'hashicorp') {
      fields.vault = 'hashicorp'
      fields.vaultPath = vaultPath
    }
    void addRustdeskAccount(fields)
      .then((next) => {
        setBusy(false)
        setLabel('')
        setUsername('')
        setPassword('')
        setVaultPath('')
        onChanged(next)
        toast('Account added.', 'RustDesk')
      })
      .catch((error: unknown) => {
        setBusy(false)
        toast(failed(error), 'RustDesk', 'error')
      })
  }

  const remove = (id: number) => {
    const sure = window.confirm('Remove this administrator account? It stays in the database, inactive, for audit.')
    if (!sure) return
    void removeRustdeskAccount(id)
      .then((next) => {
        onChanged(next)
        toast('Removed.', 'RustDesk')
      })
      .catch((error: unknown) => toast(failed(error), 'RustDesk', 'error'))
  }

  return (
    <div className="rd-card">
      <h4>Administrator accounts</h4>
      <p className="rd-note" style={{ marginBottom: 12 }}>
        {/*
          Cerinta 64: conectare si cu credentiale de administrator separate.
          RustDesk intra pe sesiunea utilizatorului; contul de aici e cel cu care
          tehnicianul ridica drepturile (UAC) inauntrul sesiunii.
        */}
        Separate accounts for working with administrator rights inside a session (UAC elevation). The technician sees
        the account name on the connect page, never its password: passwords stay in the vault (encrypted here, or in
        HashiCorp Vault) and every use is written to the credential audit log. Removed accounts are kept inactive for
        audit.
      </p>

      {page.adminAccounts.length === 0 ? (
        <p className="rd-note" style={{ margin: '0 0 12px' }}>
          No administrator account yet.
        </p>
      ) : (
        <table className="table table-hover" style={{ marginBottom: 12 }}>
          <thead>
            <tr>
              <th>Label</th>
              <th style={{ width: 220 }}>Username</th>
              <th style={{ width: 130 }}>Vault</th>
              <th style={{ width: 160 }}>Added</th>
              {page.canWrite ? <th style={{ width: 90 }}></th> : null}
            </tr>
          </thead>
          <tbody>
            {page.adminAccounts.map((account) => (
              <tr key={account.id}>
                <td>{account.label !== '' ? account.label : '—'}</td>
                <td className="rd-mono">{account.username}</td>
                <td className="rd-note">{(account.vault ?? account.provider) === 'hashicorp' ? 'HashiCorp Vault' : 'Platform (encrypted)'}</td>
                <td className="rd-note">{shortStamp(account.createdAt)}</td>
                {page.canWrite ? (
                  <td>
                    <button type="button" className="btn btn-default btn-xs rdCredDelete" onClick={() => remove(account.id)}>
                      <i className="far fa-trash-alt"></i> Remove
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {page.canWrite ? (
        canAdd ? (
          <div className="row">
            {hasHashicorp && (
              <div className="col-md-12" style={{ marginBottom: 8 }}>
                <select
                  className="form-control input-sm"
                  id="rdCredVault"
                  aria-label="Vault"
                  style={{ width: 'auto', display: 'inline-block' }}
                  value={vault}
                  onChange={(e) => setVault(e.target.value)}
                >
                  {page.canStorePasswords && <option value="local">Platform vault (encrypted)</option>}
                  <option value="hashicorp">HashiCorp Vault</option>
                </select>{' '}
                {vault === 'hashicorp' && (
                  <input
                    type="text"
                    className="form-control input-sm"
                    id="rdCredPath"
                    aria-label="Vault path"
                    placeholder="KV path, e.g. rustdesk/branch-admin"
                    style={{ width: 320, display: 'inline-block' }}
                    value={vaultPath}
                    onChange={(e) => setVaultPath(e.target.value)}
                  />
                )}
              </div>
            )}
            <div className="col-md-3">
              <input
                type="text"
                className="form-control input-sm"
                id="rdCredLabel"
                aria-label="Label"
                placeholder="Label (e.g. Branch admin)"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <input
                type="text"
                className="form-control input-sm"
                id="rdCredUser"
                aria-label="Username"
                placeholder="Username (DOMAIN\user)"
                autoComplete="off"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <input
                type="password"
                className="form-control input-sm"
                id="rdCredPass"
                aria-label="Password"
                placeholder={vault === 'hashicorp' ? 'Password (empty = already in Vault)' : 'Password'}
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>
            <div className="col-md-3">
              <button type="button" className="btn btn-info btn-sm" id="rdCredAdd" disabled={busy} onClick={add}>
                <i className="fas fa-plus"></i> Add account
              </button>
            </div>
          </div>
        ) : (
          /* Fara cheie de cifrare, un cont nou nu se poate scrie - si se spune
             INAINTE, nu dupa ce omul a tastat parola. */
          <p className="rd-note" style={{ margin: 0 }}>
            <i className="fas fa-exclamation-circle"></i> This installation has no encryption key, so a password cannot
            be stored. New accounts cannot be added until one is configured.
          </p>
        )
      ) : null}
    </div>
  )
}

function DevicesCard({ page }: { page: RustdeskShell }) {
  return (
    <div className="rd-card">
      <h4>Stations that reported in</h4>
      <p className="rd-note" style={{ marginBottom: 12 }}>
        Filled in by the agent on each start &mdash; nothing here is typed by hand.
      </p>
      <StationsTable devices={page.devices} />
    </div>
  )
}

export const STATIONS_TABLE_ID = 'rdStationsTable'

function escHtml(value: unknown): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

/**
 * Tabelul standard al listelor (`ui/DataTable`, acelasi cu External service
 * providers si Contacts): cautare, sortare pe coloane, paginare, "Showing x
 * to y of z", asezarea tinuta minte. Numele aparatului deschide fisa lui
 * intr-o fila noua.
 */
export function stationColumns(): TableColumn[] {
  const dash = (value: string) => (value !== '' ? escHtml(value) : '—')
  return [
    {
      index: 0,
      title: 'Station',
      orderable: true,
      visible: true,
      html: (row) => {
        const id = Number(row.deviceId ?? 0)
        const name = escHtml(row.name)
        return id > 0 ? `<a href="/app/inventoree/device/${id}" target="_blank" rel="noopener">${name}</a>` : name
      },
    },
    { index: 1, title: 'RustDesk ID', orderable: true, visible: true, width: 150, html: (row) => `<span class="rd-mono">${dash(String(row.rustdeskId ?? ''))}</span>` },
    { index: 2, title: 'Address', orderable: true, visible: true, width: 150, html: (row) => `<span class="rd-mono">${dash(String(row.ip ?? ''))}</span>` },
    { index: 3, title: 'Last reported', orderable: true, visible: true, width: 160, html: (row) => `<span class="rd-note">${escHtml(shortStamp(String(row.lastReportedAt ?? '')))}</span>` },
  ]
}

const STATION_SORT: Array<(device: RustdeskDevice) => string> = [
  (d) => d.name.toLowerCase(),
  (d) => d.rustdeskId,
  (d) => d.ip,
  (d) => d.lastReportedAt,
]

/**
 * Cautarea, ordonarea si pagina se fac AICI, pe client: `/settings/rustdesk`
 * intoarce toata lista (`DbalRustdeskSettingsSource::devices`, fara LIMIT,
 * `ORDER BY last_reported_at DESC`) - api-ul a ramas neatins. Ordinea
 * implicita e tot a serverului: ultima raportare, cele mai noi intai.
 */
export function stationRows(devices: RustdeskDevice[], state: TableState): { total: number; rows: RustdeskDevice[] } {
  const q = state.search.trim().toLowerCase()
  const filtered =
    q === ''
      ? devices
      : devices.filter((d) => `${d.name} ${d.rustdeskId} ${d.ip} ${d.lastReportedAt}`.toLowerCase().includes(q))
  const key = STATION_SORT[state.order.column]
  const sorted = key
    ? [...filtered].sort((a, b) => (state.order.dir === 'asc' ? 1 : -1) * key(a).localeCompare(key(b)))
    : filtered
  return { total: sorted.length, rows: sorted.slice(state.start, state.start + state.length) }
}

function StationsTable({ devices }: { devices: RustdeskDevice[] }) {
  const [state, setState] = useTableState(STATIONS_TABLE_ID, { start: 0, length: 25, order: { column: 3, dir: 'desc' }, search: '' })
  const cols = useMemo(() => stationColumns(), [])
  const shown = useMemo(() => stationRows(devices, state), [devices, state])

  return (
    <DataTable
      id={STATIONS_TABLE_ID}
      columns={cols}
      rows={shown.rows as unknown as Record<string, unknown>[]}
      total={shown.total}
      fetching={false}
      state={state}
      onState={setState}
      rowId={(row) => `station_${Number(row.id)}`}
      emptyHint="No station has reported a RustDesk identity yet. Until one does, there is nothing to connect to."
    />
  )
}

function ToolCard({ page, onChanged }: { page: RustdeskShell; onChanged(next: RustdeskShell): void }) {
  const isRustdesk = page.activeTool === 'rustdesk'

  const pick = (tool: RustdeskTool) => {
    const sure = window.confirm(
      tool === 'rustdesk'
        ? 'Every connect button in the portal will open RustDesk from now on.'
          + ' ScreenConnect stays untouched and you can switch back. Continue?'
        : 'Connect buttons go back to ScreenConnect. Continue?',
    )
    if (!sure) return
    void switchRustdeskTool(tool)
      .then((next) => {
        onChanged(next)
        toast('Switched.', 'RustDesk')
      })
      .catch((error: unknown) => toast(failed(error), 'RustDesk', 'error'))
  }

  return (
    <div className="rd-card">
      <h4>Which tool the portal uses</h4>
      <p className="rd-note" style={{ marginBottom: 12 }}>
        This is the only switch that changes anything for the people using the portal. Everything above can be filled in
        and saved without it.
      </p>
      <div className="rd-switch">
        <button
          type="button"
          className={`btn btn-sm ${isRustdesk ? 'btn-default' : 'btn-info'} rdSwitch`}
          disabled={!isRustdesk}
          onClick={() => pick('screenconnect')}
        >
          <i className="fas fa-desktop"></i> ScreenConnect
        </button>
        <button
          type="button"
          className={`btn btn-sm ${isRustdesk ? 'btn-info' : 'btn-default'} rdSwitch`}
          disabled={isRustdesk || !page.usable}
          onClick={() => pick('rustdesk')}
        >
          <i className="fas fa-bolt"></i> RustDesk
        </button>
        {!page.usable ? (
          <span className="rd-note" style={{ marginLeft: 6 }}>
            <i className="fas fa-exclamation-circle"></i> {page.whyNotUsable}
          </span>
        ) : null}
      </div>
    </div>
  )
}

export function RustdeskProviderSettings({ servers }: { servers?: ReactNode }) {
  const query = useQueryClient()
  const shell = useQuery(rustdeskShellQuery)
  const [live, setLive] = useState<RustdeskShell | null>(null)
  const [config, setConfig] = useState<RustdeskConfig | null>(null)
  const [busy, setBusy] = useState(false)

  const page = live ?? shell.data ?? null

  useEffect(() => {
    if (page && !config) setConfig(page.settings)
  }, [page, config])

  if (shell.error instanceof ApiError && shell.error.status === 403) return <Forbidden />
  if (!page || !config) return <Spinner />

  const took = (next: RustdeskShell) => {
    setLive(next)
    setConfig(next.settings)
    void query.invalidateQueries({ queryKey: ['settings', 'rustdesk'] })
  }

  const submit = (event: FormEvent) => {
    event.preventDefault()
    setBusy(true)
    const fields: Record<string, string> = {
      hbbs_host: config.hbbs_host,
      hbbs_port: String(config.hbbs_port),
      hbbr_host: config.hbbr_host,
      hbbr_port: String(config.hbbr_port),
      server_key: config.server_key,
      recording_days: String(config.recording_days),
    }
    /* Bifele nebifate NU se trimit, ca in formularul vechi: serverul citeste
       lipsa lor ca "am scos-o". */
    for (const flag of FLAGS) {
      if (config[flag.name]) fields[String(flag.name)] = '1'
    }
    /* Trimisa mereu (si 0): serverul o scrie numai cand o primeste. */
    if (config.permanent_password !== undefined) fields.permanent_password = config.permanent_password ? '1' : '0'

    void saveRustdesk(fields)
      .then((next) => {
        setBusy(false)
        took(next)
        toast('Saved.', 'RustDesk')
      })
      .catch((error: unknown) => {
        setBusy(false)
        toast(failed(error), 'RustDesk', 'error')
      })
  }

  const isRustdesk = page.activeTool === 'rustdesk'

  return (
    <div id="content">
      <h3 style={{ marginTop: 0 }}>RustDesk servers</h3>
      <p className="rd-note" style={{ margin: '0 0 16px', fontSize: 'var(--od-text-sm)' }}>
        Settings of the service provider. Each organization can choose its own install mode, allowed IPs and servers
        under RustDesk.
      </p>

      <div className={isRustdesk ? 'rd-live rd-live-rd' : 'rd-live rd-live-sc'}>
        <strong>
          <i className={isRustdesk ? 'fas fa-bolt' : 'fas fa-desktop'}></i> Currently in use:{' '}
          {isRustdesk ? 'RustDesk' : 'ScreenConnect'}
        </strong>
        <div className="rd-note" style={{ marginTop: 4 }}>
          {isRustdesk
            ? 'Connect buttons across the portal open RustDesk. ScreenConnect is untouched and can be switched back to at any time.'
            : 'Nothing has changed for anybody yet. Everything below can be filled in and tested; the switch at the bottom is what makes it live.'}
        </div>
      </div>

      {servers}

      <form id="rdForm" onSubmit={submit}>
        <ServerCard config={config} onChange={setConfig} />
        <SessionCard config={config} onChange={setConfig} />
        {page.canWrite ? (
          <div className="rd-org-actions" style={{ margin: '0 0 18px' }}>
            <button className="btn btn-success" id="rdSave" type="submit" disabled={busy}>
              Save
            </button>
          </div>
        ) : null}
      </form>

      <AccountsCard page={page} onChanged={took} />
      <DevicesCard page={page} />
      {page.canWrite ? <ToolCard page={page} onChanged={took} /> : null}
    </div>
  )
}
